<template>
    <div
        class="input-range-component-two-knobbed"
        :class="classInputRangeTwoKnobbed"
    >
        <div
            v-if="min !== max"
            class="selected-range-indicator-container"
        >
            <div
                class="clamp-spacer left"
                :style="styleClampSpacerLeft"
            ></div>

            <div
                ref="indicatorElement"
                class="selected-range-indicator"
                @mousedown.prevent.stop="select($event as any)"
            >
                <div
                    class="clamp-spacer left"
                    :style="styleSelectedClampSpacerLeft"
                ></div>

                <div class="track"></div>

                <div class="range"></div>

                <div
                    class="clamp-spacer right"
                    :style="styleSelectedClampSpacerRight"
                ></div>
            </div>

            <div
                class="clamp-spacer right"
                :style="styleClampSpacerRight"
            ></div>
        </div>

        <InputRange
            :id="`${id}-0`"
            v-qa="'input-0'"
            v-qa.model="JSON.stringify(valueMin)"
            class="input-0"
            :class="getClassRangeInput(0)"
            :clamp
            :disabled
            :max="maxFinal"
            :min="minFinal"
            :model-value="valueMinUi"
            :step
            :value-formatter="(val: number) => valueFormatter(val, 0)"
            is-part-of-two-knobbed
            @change="onChangeMin"
            @update:model-value="updateSingleInputUiValue($event as number, 0)"
        />

        <InputRange
            :id="`${id}-1`"
            v-qa="'input-1'"
            v-qa.model="JSON.stringify(valueMax)"
            class="input-1"
            :class="getClassRangeInput(1)"
            :clamp
            :disabled
            :max="maxFinal"
            :min="minFinal"
            :model-value="valueMaxUi"
            :step
            :value-formatter="(val: number) => valueFormatter(val, 1)"
            is-part-of-two-knobbed
            @change="onChangeMax"
            @update:model-value="updateSingleInputUiValue($event as number, 1)"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRef } from 'vue'

import { useNextId } from '@/helpers/registry'
import useInputRange from './useInputRange'
import { useInputRangeLazy } from './useInputRangeLazy'
import { useInputRangeTwoKnobbedMouse } from './useInputRangeTwoKnobbedMouse'
import { useInputRangeTwoKnobbedStyle } from './useInputRangeTwoKnobbedStyle'

import InputRange from './InputRange.vue'
import {
    defaults,
    type InputElementIndex,
    type InputRangeModel,
    type Props,
    type UpdateSingleInputUiValue,
} from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'
import { useInputRangeModels } from '@/components/ui/inputs/range/useInputRangeModel.ts'

const props = withDefaults(defineProps<Props>(), defaults)

const emit = defineEmits<{
    'update:modelValue': [InputRangeModel]
}>()

const {
    minFinal,
    maxFinal,
    rangeClamped,
    styleClampSpacerLeft,
    styleClampSpacerRight,
} = useInputRange(props)

const id = `2knob-${useNextId()}`

const { min: valueMin, max: valueMax } = useInputRangeModels(
    () => props.modelValue,
    minFinal,
    maxFinal,
    (model: InputRangeModel) => {
        emit('update:modelValue', model)
    }
)

const { valueUi: valueMinUi, onChange: onChangeMin } = useInputRangeLazy(
    valueMin,
    true
)
const { valueUi: valueMaxUi, onChange: onChangeMax } = useInputRangeLazy(
    valueMax,
    true
)

const activeElement = ref<InputElementIndex>(0)

const updateSingleInputUiValue: UpdateSingleInputUiValue = (
    value: number,
    index?: InputElementIndex
) => {
    if (index !== undefined) {
        activeElement.value = index
    }

    switch (activeElement.value) {
        case 0:
            valueMinUi.value = value
            break
        case 1:
            valueMaxUi.value = value
            break
    }
}

const { styleSelectedClampSpacerLeft, styleSelectedClampSpacerRight } =
    useInputRangeTwoKnobbedStyle(minFinal, rangeClamped, valueMinUi, valueMaxUi)

const { indicatorElement, select } = useInputRangeTwoKnobbedMouse(
    valueMinUi,
    valueMaxUi,
    onChangeMin,
    onChangeMax,
    updateSingleInputUiValue,
    minFinal,
    toRef(() => props.step as number),
    rangeClamped,
    toRef(() => props.disabled)
)

const classInputRangeTwoKnobbed = computed(() => ({
    disabled: props.disabled,
}))

const getClassRangeInput = (index: InputElementIndex) => ({
    active: index === activeElement.value,
})
</script>

<style lang="scss" scoped>
.input-range-component-two-knobbed {
    position: relative;
    height: 70px;
    padding-top: 16px;

    &.disabled {
        cursor: not-allowed !important;

        .range {
            background-color: $grey-200 !important;
            cursor: not-allowed !important;
        }

        .track {
            background-color: $grey-200 !important;
            box-shadow: none !important;
            cursor: not-allowed !important;
        }
    }

    .input-0,
    .input-1 {
        position: absolute;
        width: 100%;
        pointer-events: none;
        z-index: 1;

        &.active {
            z-index: 2;
        }
    }

    .selected-range-indicator-container {
        position: absolute;
        top: 41px;
        width: 100%;
        height: 4px;
        display: flex;

        .clamp-spacer {
            pointer-events: none;

            &.left {
                flex: 0 0 0%;
            }

            &.right {
                flex: 0 0 0%;
            }
        }

        .selected-range-indicator {
            position: relative;
            flex: 1 1 auto;
            display: flex;
            margin-top: -4px;
            height: 12px;

            .track {
                position: absolute;
                margin-top: 4px;
                width: 100%;
                height: 4px;
                border-radius: 2px;
                background: $grey-200;
                box-shadow: inset 0 1px 1px rgb(0 0 0 / 10%);
                cursor: pointer;
            }

            .range {
                position: relative;
                margin-top: 4px;
                flex: 1 1 auto;
                height: 4px;
                background-color: $slate;
                pointer-events: none;
                cursor: pointer;
            }
        }
    }

    .min-max,
    .text-inputs {
        position: absolute;
        width: 100%;
        display: flex;
        justify-content: space-between;

        font-family: $effra-font-stack;
        font-weight: 400;
        font-size: 0.875rem;
        line-height: 0.875rem;
        text-align: center;
        letter-spacing: 0.05em;
        text-transform: uppercase;
    }

    .min-max {
        bottom: 68px;
    }

    .text-inputs {
        bottom: 10px;

        .text-input {
            width: 80px;
            height: 40px;
            border: 1px solid $grey-200;
            border-radius: 12px;
        }

        input[type='number'] {
            -moz-appearance: textfield;
            appearance: textfield;
            margin: 0;
        }
    }
}
</style>
